import {
  Box,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  SeV2QuestionKind,
  SeV2QuestionStatus,
  SourcingEventV2Dto,
  useGetBuyerAnnouncementsQuery,
  useGetBuyerSeV2QuestionsQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { BuyerQuestionInbox } from "./BuyerQuestionInbox";
import { ClarificationsPanel } from "./ClarificationsPanel";
import { InformationComposer } from "./InformationComposer";
import { PublicFeedPreview } from "./PublicFeedPreview";

export const CommunicationTab = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const eventId = event.id;

  const {
    data: questionsData,
    error: questionsError,
    isLoading: questionsLoading,
  } = useGetBuyerSeV2QuestionsQuery({
    eventId,
  });
  const {
    data: announcementsData,
    error: announcementsError,
    isLoading: announcementsLoading,
  } = useGetBuyerAnnouncementsQuery({ eventId });

  useEffect(() => {
    if (questionsError) displayer.triggerSoft(questionsError);
  }, [questionsError, displayer]);
  useEffect(() => {
    if (announcementsError) displayer.triggerSoft(announcementsError);
  }, [announcementsError, displayer]);

  if (questionsLoading || announcementsLoading) {
    return (
      <Box py="10" textAlign="center">
        <Spinner color="smPrimary" />
      </Box>
    );
  }

  const threads = questionsData?.questions ?? [];
  // Supplier questions and buyer-opened clarification dialogs share an aggregate but not a tab: a question is
  // competition Q&A that may end up published to all, a clarification is a private 1:1 exchange (FOA § 23-5).
  const questions = threads.filter((q) => q.kind === SeV2QuestionKind.Question);
  const clarifications = threads.filter((q) => q.kind === SeV2QuestionKind.Clarification);
  const announcements = announcementsData?.announcements ?? [];
  const unanswered = questions.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;
  const awaitingTenderer = clarifications.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;

  // Split announcements by source: an announcement is a published Q&A if a question points to it; otherwise standalone.
  const publishedAnnouncementIds = new Set(
    questions
      .map((q) => q.publishedAnnouncementId)
      .filter((id): id is string => !!id),
  );
  const qaAnnouncements = announcements.filter((a) =>
    publishedAnnouncementIds.has(a.id),
  );
  const infoAnnouncements = announcements.filter(
    (a) => !publishedAnnouncementIds.has(a.id),
  );

  return (
    <Tabs colorScheme="teal" isLazy>
      <TabList>
        <Tab>
          {t("Questions")}
          {unanswered > 0 ? ` (${unanswered})` : ""}
        </Tab>
        <Tab>
          {t("Public Q&A")}
          {qaAnnouncements.length > 0 ? ` (${qaAnnouncements.length})` : ""}
        </Tab>
        <Tab>
          {t("Information to tenderers")}
          {infoAnnouncements.length > 0 ? ` (${infoAnnouncements.length})` : ""}
        </Tab>
        <Tab>
          {t("Clarifications")}
          {awaitingTenderer > 0 ? ` (${awaitingTenderer})` : ""}
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px="0">
          <BuyerQuestionInbox eventId={eventId} questions={questions} />
        </TabPanel>
        <TabPanel px="0">
          <PublicFeedPreview eventId={eventId} announcements={qaAnnouncements} />
        </TabPanel>
        <TabPanel px="0">
          <InformationComposer
            eventId={eventId}
            announcements={infoAnnouncements}
          />
        </TabPanel>
        <TabPanel px="0">
          <ClarificationsPanel event={event} questions={clarifications} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
